import { httpGet, httpPost } from '../../../../services/api/AmplifyApi'
import { restApiKey } from '../../../../_constants/Code'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { parseCsvWithWorker } from '../../FileManagement/extra/services'
import { ValidationResultResponseSchema } from './types'
import {
  _snakeToCamel,
  _transformMetaResponse,
  _transformFileTypesResponse,
  _transformMappingConfigResponse,
  feTypeToBeMappingType,
  itemToMappingInfoForPayload,
  normalizeDeepKeysToCamel
} from './helpers'

/** Redux/store から法人番号を取得 */
const _getCorporationNumber = (store) => {
  const state = store.getState?.() ?? {}
  const root = state.mainStore ?? state
  const corporationNumber = root?.company?.corporationNumber ?? ''
  return { corporationNumber }
}

/** GET 共通: pathGetter(corporationNumber) でパス解決して API 呼び出し */
const _apiGet = (store, pathGetter, queryStringParameters = {}) => {
  const { corporationNumber } = _getCorporationNumber(store)
  const path = typeof pathGetter === 'function' ? pathGetter(corporationNumber) : pathGetter

  return httpGet(restApiKey.CHROFY_REST_API, path, {
    headers: {},
    queryStringParameters
  })
}

/** POST 共通: pathGetter(corporationNumber) でパス解決して API 呼び出し */
const _apiPost = (store, pathGetter, body) => {
  const { corporationNumber } = _getCorporationNumber(store)
  const path = typeof pathGetter === 'function' ? pathGetter(corporationNumber) : pathGetter
  return httpPost(restApiKey.CHROFY_REST_API, path, { headers: {}, body })
}

/**
 * データ種類ステータス取得（IF02095）。データ検証タブ表示時・「結果を更新」押下時（SR-052, SR-055）
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; dataKindCode: string }} params
 * @returns {Promise<string | null>}
 */
export async function fetchDataKindStatus(store, params) {
  const { dataCreateTaskKey, dataKindCode } = params
  const query = {
    data_create_task_key: Number(dataCreateTaskKey)
  }
  const raw = await _apiGet(store, PathInfoList.Resource.getGenericTaskScreen, query)
  // IF02095 は集約のため、status_by_data_kind から該当データ種類のステータスのみ抽出
  const body = raw && typeof raw === 'object' ? raw : {}
  const rootLayer = normalizeDeepKeysToCamel(body, { deep: false }) || {}
  const statusSrc = rootLayer.statusByDataKind ?? body.status_by_data_kind
  if (statusSrc && typeof statusSrc === 'object' && !Array.isArray(statusSrc)) {
    return statusSrc[dataKindCode] ?? null
  }
  return null
}

/**
 * ベースファイル設定有無の取得（IF02095）。検証実行ゲート（バグ#3160）用。
 *
 * `is_base_file_set` は IF02095（get_generic_task_screen）が期間対応で解決済み （月次は自タスク、四半期/年次は対応する月次タスクのベースを参照）。検証実行ボタンの
 * 非活性判定は本フラグに一本化し、IF02086 の validation_gate には依存しない。 取得失敗時は AnalysisData の既定（false）と揃え、安全側（ベース未指定扱い）に倒す。
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string }} params
 * @returns {Promise<boolean>}
 */
export async function fetchBaseFileSet(store, params) {
  const { dataCreateTaskKey } = params
  const query = {
    data_create_task_key: Number(dataCreateTaskKey)
  }
  try {
    const raw = await _apiGet(store, PathInfoList.Resource.getGenericTaskScreen, query)
    const body = raw && typeof raw === 'object' ? raw : {}
    const rootLayer = normalizeDeepKeysToCamel(body, { deep: false }) || {}
    const dct = rootLayer.dataCreateTask ?? body.data_create_task ?? {}
    return Boolean(dct.isBaseFileSet ?? dct.is_base_file_set ?? false)
  } catch {
    return false
  }
}

/**
 * メタ情報取得（IF02083）
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; genericKindCode: string }} params
 * @returns {Promise<import('./types').Meta>}
 */
export async function fetchMeta(store, params) {
  const { dataCreateTaskKey, genericKindCode } = params
  const query = {
    data_create_task_key: Number(dataCreateTaskKey),
    generic_task_code: genericKindCode ?? ''
  }
  const metaRaw = await _apiGet(store, PathInfoList.Resource.getGenericTaskMetaInfo, query)
  return _transformMetaResponse(metaRaw)
}

/**
 * ファイル種別取得（IF02084）
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; genericKindCode?: string }} params GenericKindCode がある場合は data_kind_code
 *   としてクエリに付与（単一ファイル種別データ分類で結合キー列を保持するため）
 * @returns {Promise<import('./types').FileTypeInfo[]>}
 */
export async function fetchFileTypes(store, params) {
  const query = {
    data_create_task_key: Number(params.dataCreateTaskKey),
    ...(params.genericKindCode ? { data_kind_code: params.genericKindCode } : {})
  }
  const fileTypesRaw = await _apiGet(store, PathInfoList.Resource.getGenericTaskFileClassification, query)
  return _transformFileTypesResponse(fileTypesRaw)
}

/**
 * 初期表示: IF02083 + IF02084 + IF02085 を Promise.all で並列実行（SR-048）
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; genericKindCode: string }} params
 * @returns {Promise<{
 *   meta: import('./types').Meta
 *   fileTypes: import('./types').FileTypeInfo[]
 *   mappingData: import('./types').MappingData
 *   skippedFields: import('./types').SkippedFields
 *   genericColumns: import('./types').GenericColumnWithRule[]
 *   ruleAppliedFields: Record<string, boolean>
 * }>}
 */
export async function fetchInitialData(store, params) {
  const [meta, fileTypes, mappingConfigResult] = await Promise.all([
    fetchMeta(store, params),
    fetchFileTypes(store, params),
    fetchMappingConfig(store, params).catch(() => ({
      mappingData: {},
      skippedFields: {},
      genericColumns: [],
      ruleAppliedFields: {}
    }))
  ])
  const { mappingData, skippedFields, genericColumns, ruleAppliedFields = {} } = mappingConfigResult
  const metaColMap = new Map(
    (meta.genericChrofyColumns ?? []).map((c) => [`${c.genericTaskCode ?? ''}_${c.chrofyColumnCode ?? ''}`, c])
  )
  const genericColumnsWithRule = genericColumns.map((col) => {
    const key = `${col.genericTaskCode}_${col.chrofyColumnCode}`
    const metaCol = metaColMap.get(key)
    return {
      ...col,
      hasRule: metaCol?.hasJudgmentRule === true,
      isRequired: metaCol?.isRequired === true,
      chrofyColumnDescription: metaCol?.chrofyColumnDescription,
      chrofyColumnDataType: metaCol?.chrofyColumnDataType,
      chrofyColumnSample: metaCol?.chrofyColumnSample,
      valueMappingColumns: metaCol?.valueMappingColumns
    }
  })
  return {
    meta,
    fileTypes,
    mappingData,
    skippedFields,
    genericColumns: genericColumnsWithRule,
    ruleAppliedFields
  }
}

/**
 * マッピング設定取得（IF02085）。単体で呼ぶ場合用
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; genericTaskCode?: string }} params
 * @returns {Promise<{
 *   mappingData: import('./types').MappingData
 *   skippedFields: import('./types').SkippedFields
 *   genericColumns: import('./types').GenericColumnWithRule[]
 *   ruleAppliedFields: Record<string, boolean>
 * }>}
 */
export async function fetchMappingConfig(store, params) {
  const { dataCreateTaskKey, genericKindCode = '' } = params
  const query = {
    data_create_task_key: dataCreateTaskKey,
    generic_task_code: genericKindCode ?? ''
  }
  const raw = await _apiGet(store, PathInfoList.Resource.getGenericTaskSetting, query)
  return _transformMappingConfigResponse(raw)
}

/** IF02086 生レスポンスを camelCase 化してスキーマ検証用に整える */
const _normalizeValidationResult = (raw) => {
  const r = _snakeToCamel(/** @type {Record<string, unknown>} */ (raw))
  if (r.summary && typeof r.summary === 'object') {
    r.summary = _snakeToCamel(/** @type {Record<string, unknown>} */ (r.summary))
  }
  if (r.results && typeof r.results === 'object') {
    const res = /** @type {Record<string, unknown>} */ (r.results)
    if (Array.isArray(res.warnings)) {
      res.warnings = res.warnings.map((w) => _snakeToCamel(/** @type {Record<string, unknown>} */ (w)))
    }
    if (Array.isArray(res.errors)) {
      res.errors = res.errors.map((e) => _snakeToCamel(/** @type {Record<string, unknown>} */ (e)))
    }
    r.results = res
  }
  // validation_gate の内部キー（can_validate）を camelCase に揃える。
  // 親側 _snakeToCamel は 1 階層しか変換しないため、ネストオブジェクトはここで明示変換する。
  if (r.validationGate && typeof r.validationGate === 'object') {
    r.validationGate = _snakeToCamel(/** @type {Record<string, unknown>} */ (r.validationGate))
  }
  return ValidationResultResponseSchema.parse(r)
}

/**
 * 検証結果取得（IF02086）。データ検証タブ表示時・「結果を更新」押下時（SR-052, SR-055）
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; dataKindCode: string }} params
 * @returns {Promise<import('./types').ValidationResultResponse>}
 */
export async function fetchValidationResult(store, params) {
  const query = {
    data_create_task_key: params.dataCreateTaskKey,
    data_kind_code: params.dataKindCode
  }
  const raw = await _apiGet(store, PathInfoList.Resource.getDataValidationResult, query)
  return _normalizeValidationResult(raw)
}

/**
 * 署名付きURLからプレビューCSVを取得し、Workerでパースする
 *
 * マッピング結果CSVは、日付・時刻変換の失敗などで全セルが空になった行も含む（バグ#3186）。 検証詳細一覧の行番号と結果テーブルを突合できるよう、`keepEmptyLines: true` で空行も1データ行として保持する。
 *
 * @param {string} presignedUrl
 * @returns {Promise<{ headers: string[]; records: string[][]; totalRows: number }>}
 */
export async function fetchAndParsePreviewCsv(presignedUrl) {
  const response = await fetch(presignedUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch preview csv: ${response.status}`)
  }
  const csvText = await response.text()
  return parseCsvWithWorker(csvText, { keepEmptyLines: true })
}

/**
 * 保存用 mappings 配列を構築（IF02087）。同一 chrofy_column_code で複数優先度は items ごとに 1 件。
 *
 * @param {{
 *   mappingData?: import('./types').MappingData
 *   skippedFields?: import('./types').SkippedFields
 *   genericColumns?: import('./types').GenericColumnWithRule[]
 * }} payload
 * @param {{ dataKindCode?: string }} params
 * @returns {{
 *   chrofy_column_code: string
 *   priority: number
 *   num_agg_type: string
 *   mapping_type: string
 *   mapping_info: Record<string, unknown>
 *   page_state: unknown
 * }[]}
 */
const _buildMappings = (payload, params) => {
  const genericColumns = payload.genericColumns ?? []
  const columns = genericColumns.filter((c) => String(c.genericTaskCode ?? '') === String(params.dataKindCode ?? ''))
  const { mappingData = {}, skippedFields = {}, ruleAppliedFields = {} } = payload
  const mappings = []

  for (const col of columns) {
    const fieldKey = `${col.genericTaskCode}_${col.chrofyColumnCode}`
    const skipped = Boolean(skippedFields[fieldKey])
    const ruleApplied = Boolean(ruleAppliedFields[fieldKey])
    const entry = mappingData[fieldKey]

    if (skipped) {
      mappings.push({
        chrofy_column_code: col.chrofyColumnCode,
        priority: 1,
        num_agg_type: 'priority',
        mapping_type: 'remove',
        mapping_info: { mapping_type: 'remove' },
        page_state: null
      })
      continue
    }

    if (ruleApplied) {
      mappings.push({
        chrofy_column_code: col.chrofyColumnCode,
        priority: 1,
        num_agg_type: 'priority',
        mapping_type: 'judgment_rule',
        mapping_info: {},
        page_state: null
      })
      continue
    }

    const items = entry?.items ?? []
    const numAggType = entry?.mode === 'sum' ? 'total' : 'priority'
    if (items.length === 0) continue

    items.forEach((item, index) => {
      mappings.push({
        chrofy_column_code: col.chrofyColumnCode,
        priority: index + 1,
        num_agg_type: numAggType,
        mapping_type: feTypeToBeMappingType(item.type || 'simple'),
        mapping_info: itemToMappingInfoForPayload(item),
        page_state: item.page_state ?? item.pageState ?? null
      })
    })
  }
  return mappings
}

/**
 * マッピング設定保存（IF02087）。未保存確認モーダル「保存して移動/戻る」時（SR-053） payload.genericColumns は呼び出し元で渡すこと。
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; dataKindCode: string }} params
 * @param {{
 *   mappingData?: import('./types').MappingData
 *   skippedFields?: import('./types').SkippedFields
 *   genericColumns?: import('./types').GenericColumnWithRule[]
 * }} payload
 * @returns {Promise<void>}
 */
export async function saveMapping(store, params, payload) {
  const mappings = _buildMappings(payload, params)
  const body = {
    data_create_task_key: Number(params.dataCreateTaskKey),
    data_kind_code: params.dataKindCode,
    mappings
  }
  await _apiPost(store, PathInfoList.Resource.updateGenericTaskSetting, body)
}

/** IF02088 レスポンスから実行日時・ユーザー名を抽出 */
const _parseStartValidationResponse = (raw) => {
  const rawObj = raw != null && typeof raw === 'object' ? raw : {}
  const bodyObj =
    rawObj.data != null && typeof rawObj.data === 'object'
      ? /** @type {Record<string, unknown>} */ (rawObj.data)
      : /** @type {Record<string, unknown>} */ (rawObj)
  const r = _snakeToCamel(bodyObj)
  return {
    verificationExecutedAtDateTime:
      typeof r.verificationExecutedAtDateTime === 'string'
        ? r.verificationExecutedAtDateTime
        : typeof bodyObj.verification_executed_at_datetime === 'string'
          ? bodyObj.verification_executed_at_datetime
          : undefined,
    verificationExecutedUsername:
      typeof r.verificationExecutedUsername === 'string'
        ? r.verificationExecutedUsername
        : typeof bodyObj.verification_executed_username === 'string'
          ? bodyObj.verification_executed_username
          : undefined
  }
}

/**
 * 検証実行（IF02088）。検証実行ボタン押下でバッチ起動（SR-054）
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; dataKindCode?: string }} params
 * @returns {Promise<{ verificationExecutedAtDateTime?: string; verificationExecutedUsername?: string }>}
 */
export async function startValidation(store, params) {
  const body = {
    data_create_task_key: Number(params.dataCreateTaskKey),
    data_kind_code: params.dataKindCode ?? ''
  }
  const raw = await _apiPost(store, PathInfoList.Resource.startDataValidationResult, body)
  return _parseStartValidationResponse(raw)
}

/**
 * 前月設定コピー用: 前月期間で IF02085 相当のマッピング取得（SR-057, SR-058）
 *
 * @param {any} store
 * @param {{ dataCreateTaskKey: string; genericTaskCode: string; previousTaskCode?: string }} params
 * @returns {Promise<{
 *   mappingData: import('./types').MappingData
 *   skippedFields: import('./types').SkippedFields
 *   ruleAppliedFields: Record<string, boolean>
 * }>}
 */
export async function fetchPrevMonthMapping(store, params) {
  const { dataCreateTaskKey, genericTaskCode, previousTaskCode } = params
  const query = {
    data_create_task_key: previousTaskCode ?? dataCreateTaskKey,
    ...(genericTaskCode ? { generic_task_code: genericTaskCode } : {})
  }
  const raw = await _apiGet(store, PathInfoList.Resource.getGenericTaskSetting, query)
  const { mappingData, skippedFields, ruleAppliedFields } = _transformMappingConfigResponse(raw)
  return { mappingData, skippedFields, ruleAppliedFields }
}

/**
 * マッピングプレビュー取得（IF02099: POST .../get_mapping_preview_result）。モーダル「プレビュー表示」押下時
 *
 * @param {any} store
 * @param {Record<string, unknown>} body IF02099 リクエストボディ（snake_case）
 * @returns {Promise<unknown>}
 */
export async function getMappingPreview(store, body) {
  return _apiPost(store, PathInfoList.Resource.getMappingPreview, body)
}
